import { getLeadTypeLabel } from "utils/lead-type.utils";
import { LeadStageEnum } from "enums/lead-stage.enum";
import { PivotStatTable } from "components/table/PivotStatTable";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { GraphCard } from "components/stats/GraphCard";
import { typesColors } from "constants/colors";
import { LeadTypeEnum } from "enums/lead-type.enum";
import { groupBy } from "lodash";
import { Legend } from "components/chart/Legend";
import { Row } from "react-table";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faAngleDown, faChevronRight } from "@fortawesome/pro-solid-svg-icons";
import { Box } from "@chakra-ui/react";
import { LeadStageTag, LeadTypeTag } from "components/tags";
import { LeadStageLabel } from "constants/lead-stage-label";
import { TLeadsByOffice } from "types/account-analytics.type";
import { CSVDownloadButton } from "components";
import {
  flattenDataForLeadsByStageCSV,
  formatCSVDateRangeTitle,
  IFlatOfficeLeadsByStage,
} from "utils/csv-exporter.utils";
import { TSerializedDateRange } from "types/date.type";
import {
  OFFICE_LEADS_BY_STAGE_CSV_HEADERS,
  TITLE_PREFIX_LEADS_BY_STAGE,
} from "constants/csv-exporter";

interface IProps {
  data: TLeadsByOffice[];
  dateRange: TSerializedDateRange;
}

export const OfficeLeadsStatsTable: React.FC<IProps> = ({
  data,
  dateRange,
}) => {
  const [visibleTypes, setVisibleTypes] = useState<string[]>([]);
  const allTypes = useMemo(() => {
    const types = new Set<string>();
    data.forEach(dataPoint => {
      dataPoint.stats.forEach(stat => {
        types.add(stat.type);
      });
    });

    return types;
  }, [data]);

  useEffect(() => {
    setVisibleTypes(Array.from(allTypes));
  }, [allTypes]);

  const generateColumnByStage = useCallback(
    (stage: LeadStageEnum) => {
      return {
        Header: (
          <Box display={"flex"} height={"100%"} alignItems={"center"}>
            <LeadStageTag stage={stage} />
          </Box>
        ),
        id: LeadStageLabel[stage],
        accessor: (originalRow: TLeadsByOffice) =>
          originalRow.stats.reduce((accumulator, currentValue) => {
            if (!visibleTypes.includes(currentValue.type)) {
              return accumulator;
            }

            return currentValue.stage === stage
              ? accumulator + currentValue.count
              : accumulator;
          }, 0),
        Cell: ({ row }: { row: Row<TLeadsByOffice> }) => {
          return (
            <Box display={"flex"} height={"100%"} alignItems={"center"}>
              <Box>{row.values[LeadStageLabel[stage]]}</Box>
            </Box>
          );
        },
        width: 100,
      };
    },
    [visibleTypes]
  );

  const handleToggleType = useCallback(
    (type: string) => {
      if (visibleTypes.includes(type)) {
        setVisibleTypes(visibleTypes.filter(element => element !== type));
      } else {
        setVisibleTypes([...visibleTypes, type]);
      }
    },
    [visibleTypes]
  );

  const legend = useMemo(() => {
    return (
      <Legend
        onItemClick={handleToggleType}
        items={Array.from(allTypes).map((type: any) => {
          return {
            key: type,
            label: getLeadTypeLabel(type),
            color: typesColors[type as LeadTypeEnum]?.statsColor || "#718096",
            toggled: visibleTypes.includes(type),
          };
        })}
      />
    );
  }, [allTypes, handleToggleType, visibleTypes]);

  const table = useMemo(() => {
    return (
      <PivotStatTable<TLeadsByOffice>
        getSubRows={row => {
          if (!row.officeId) return null;
          const subRows: any[] = [];
          const groupedByType = groupBy(row.stats, stat => stat.type);
          Object.keys(groupedByType).forEach(typeKey => {
            if (visibleTypes.includes(typeKey)) {
              subRows.push({
                officeName: typeKey,
                stats: groupedByType[typeKey].map(stat => ({
                  count: stat.count,
                  type: stat.type,
                  stage: stat.stage,
                })),
              });
            }
          });

          return subRows;
        }}
        data={data}
        defaultSort={[
          {
            id: "officeName",
            desc: false,
          },
        ]}
        columns={[
          {
            id: "expander",
            Header: ({
              getToggleAllRowsExpandedProps,
              isAllRowsExpanded,
            }: any) => {
              return (
                <Box display={"flex"} height={"100%"} alignItems={"center"}>
                  <div {...getToggleAllRowsExpandedProps()}>
                    {isAllRowsExpanded ? (
                      <FontAwesomeIcon icon={faAngleDown} fontSize={14} />
                    ) : (
                      <FontAwesomeIcon icon={faChevronRight} fontSize={12} />
                    )}
                  </div>
                </Box>
              );
            },
            Cell: ({ row }: any) =>
              row.canExpand ? (
                <Box display={"flex"} height={"100%"} alignItems={"center"}>
                  <div>
                    {row.isExpanded ? (
                      <FontAwesomeIcon icon={faAngleDown} fontSize={14} />
                    ) : (
                      <FontAwesomeIcon icon={faChevronRight} fontSize={12} />
                    )}
                  </div>
                </Box>
              ) : null,
            width: 20,
          },
          {
            Header: (
              <Box display={"flex"} height={"100%"} alignItems={"center"}>
                <div>Office</div>
              </Box>
            ),
            accessor: "officeName",
            Cell: ({ row }: any) => {
              return (
                <Box display={"flex"} height={"100%"} alignItems={"center"}>
                  {row.original.officeId && (
                    <div>{row.original.officeName}</div>
                  )}
                  {!row.original.officeId && (
                    <LeadTypeTag type={row.original.officeName} />
                  )}
                </Box>
              );
            },
          },
          generateColumnByStage(LeadStageEnum.UNREAD),
          generateColumnByStage(LeadStageEnum.IN_PROGRESS),
          generateColumnByStage(LeadStageEnum.BUSINESS),
          generateColumnByStage(LeadStageEnum.NO_BUSINESS),
        ]}
      />
    );
  }, [data, generateColumnByStage, visibleTypes]);

  const csvFileName = useMemo(() => {
    return formatCSVDateRangeTitle(dateRange, TITLE_PREFIX_LEADS_BY_STAGE);
  }, [dateRange]);

  const flatDataForCSVDownload = useMemo(() => {
    return flattenDataForLeadsByStageCSV(data);
  }, [data]);

  return (
    <GraphCard
      title={`Leads by office by stage`}
      action={
        <CSVDownloadButton<IFlatOfficeLeadsByStage>
          data={flatDataForCSVDownload}
          options={{
            useKeysAsHeaders: false,
            filename: csvFileName,
            headers: OFFICE_LEADS_BY_STAGE_CSV_HEADERS,
          }}
        />
      }
    >
      <Box display={"flex"} flexDirection={"row"} justifyContent={"flex-start"}>
        {legend}
      </Box>
      {table}
    </GraphCard>
  );
};
